import React, { useEffect, useState } from "react";
import { Lock, Check, ChevronDown } from "lucide-react";
import { api } from "../../lib/api";
import { useUiStore } from "../../store/uiStore";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

// Top-right trading-account selector. Switching to LIVE only changes what the user is VIEWING; every LIVE execution is
// still authorized server-side (kill switch → feature → permission → linked account → risk) — nothing here can bypass it.
export default function AccountSelector() {
  const { accountMode, setAccountMode } = useUiStore();
  const [status, setStatus] = useState(null);
  useEffect(() => {
    const load = () => api.get("/trading/status").then(({ data }) => setStatus(data)).catch(() => {});
    load(); const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);
  const demo = status?.accounts?.demo, live = status?.accounts?.live;
  const isLive = accountMode === "live";
  const bal = demo?.balance != null ? `$${Number(demo.balance).toFixed(2)} ${demo.currency || "USD"}` : "—";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="account-selector-btn" title="Trading account" aria-label="Trading account"
          className={`inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-widest ${isLive ? "bg-sell/15 text-sell border-sell/50" : "bg-buy/15 text-buy border-buy/40"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${isLive ? "bg-sell" : "bg-buy"}`} />
          <span data-testid="account-mode-label">{isLive ? "LIVE" : "DEMO"}</span>
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-1.5 bg-popover" data-testid="account-selector">
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Trading account</div>
        <button data-testid="account-option-demo" onClick={() => setAccountMode("demo")}
          className={`w-full flex items-start gap-3 px-2.5 py-2 rounded-lg text-left hover:bg-secondary ${!isLive ? "bg-buy/10" : ""}`}>
          <span className="mt-1 h-2.5 w-2.5 rounded-full bg-buy shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2 text-sm font-bold text-buy">DEMO {!isLive && <Check className="h-3.5 w-3.5" />}</span>
            <span className="block text-xs text-muted-foreground">Demo account · {demo?.account_id || "…"}</span>
            <span className="block text-xs font-mono-nums text-foreground" data-testid="account-demo-balance">{bal}</span>
          </span>
        </button>
        <button data-testid="account-option-live" onClick={() => setAccountMode("live")}
          className={`w-full flex items-start gap-3 px-2.5 py-2 rounded-lg text-left hover:bg-secondary ${isLive ? "bg-sell/10" : ""}`}>
          <Lock className="mt-1 h-3 w-3 text-sell shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2 text-sm font-bold text-sell">LIVE {isLive && <Check className="h-3.5 w-3.5" />}</span>
            <span className="block text-xs text-muted-foreground">Real account · {live?.linked ? live.account_id : "Not connected"}</span>
            <span className="block text-[11px] text-primary">Connect account → <span className="text-muted-foreground">(next phase · OAuth)</span></span>
          </span>
        </button>
        <div className="mt-1 px-2.5 py-2 rounded-lg border border-border text-[10px] leading-relaxed text-muted-foreground" data-testid="live-auth-chain">
          <div className="font-bold uppercase tracking-widest text-foreground mb-1">Live execution authorization</div>
          {(live?.chain || []).map((c) => (
            <div key={c.step} className="flex items-center gap-1.5"><span className={`h-1.5 w-1.5 rounded-full ${c.ok ? "bg-buy" : "bg-sell"}`} /><span className="capitalize">{c.step.replace("_", " ")}</span>{!c.ok && <span className="text-sell truncate">· {c.detail}</span>}</div>
          ))}
          <div className="mt-1 text-sell font-semibold" data-testid="live-locked-note">LIVE execution is server-locked{live?.reason ? ` — ${live.reason}` : ""}.</div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
